import type { Language } from '@/i18n/dictionary';
import { LANGUAGE } from '@/i18n/dictionary';

/**
 * The two USCIS pages the I-9 documents QR may encode, one per kiosk language [decision 0370].
 *
 * ⛔ **These are the only QR codes this kiosk may render.** Decision 0114 bans QR codes and live redirects
 * on every screen; 0370 narrows it for this ONE link, with exactly these two URLs. Any other QR, another
 * government page included, needs its own record against 0114 first. `UscisDocumentsQr` takes a LANGUAGE
 * rather than a URL for the same reason: there is no prop through which a different string can reach the
 * encoder.
 *
 * ⛔ **Code constants, on purpose** [KAP-6, the QR-drawn-on-the-device criterion]. Never read from the
 * dictionary, a config or the api: a URL that can be changed outside the code is a route for a shared lobby
 * device to send a candidate somewhere nobody reviewed. Each string is encoded exactly as written here —
 * no redirect of ours, no shortener, no query string.
 *
 * English is [0341]'s destination, typed by ISS (Heidi Carrel, 2026-09-23). Spanish is the Operator's,
 * 2026-09-25 [0370].
 */
export const USCIS_DOCUMENTS_URL = Object.freeze({
  [LANGUAGE.en]: 'https://www.uscis.gov/i-9-central/form-i-9-acceptable-documents',
  [LANGUAGE.es]: 'https://www.uscis.gov/es/central-i-9/documentos-aceptables-del-formulario-i-9',
} as const satisfies Record<Language, string>);

export type UscisDocumentsUrl = (typeof USCIS_DOCUMENTS_URL)[Language];

/** The page for the language the kiosk is showing at this moment [KAP-6, the Spanish-kiosk scenario]. */
export function uscisDocumentsUrl(language: Language): UscisDocumentsUrl {
  return USCIS_DOCUMENTS_URL[language];
}

/**
 * The URL as the overlay prints it under the code — the frame's *visible fallback*, for a phone that will
 * not scan [Figma · `03 · I-9 — USCIS documents QR`, `URL (visible fallback)`].
 *
 * The frame drops the scheme and the `www.`, so this does exactly that and nothing else. It is derived
 * from the constant rather than written out a second time, so the printed address and the encoded one
 * cannot drift apart. It is TEXT, never a link: nothing on the kiosk opens it.
 */
export function displayedUscisDocumentsUrl(url: UscisDocumentsUrl): string {
  return url.replace(/^https:\/\/www\./, '');
}
